// ─── Entitlements / client-type seam ─────────────────────────────────────────
//
// The UI currently has NO role / client-type concept: authSlice only stores
// { isAuthenticated, username, resetRequired } and the login response returns
// only { token, resetRequired }. This module introduces the seam so nav links
// and routes can be gated by *who the user is* (role) and *what product they
// bought* (clientType), starting with the new Industrial console.
//
// TODO(backend): once /auth/login (see LoginPage.jsx) returns `role` and
// `client_type`, thread them into authSlice and read them here via
// `hydrateFromAuth()` instead of the localStorage/env fallback below. Until
// then the values resolve from (in priority order):
//   1. values pushed in by hydrateFromAuth() (future: from login response)
//   2. localStorage  'prvsk_client_type' / 'prvsk_role'   (manual / dev override)
//   3. env           REACT_APP_CLIENT_TYPE / REACT_APP_ROLE
//   4. safe defaults ('home' / 'admin')
//
// Defaults are deliberately permissive (clientType 'home', role 'admin') so
// existing single-tenant home deployments see exactly what they see today —
// the Industrial group is purely additive and only appears for 'industrial'.

import { useSyncExternalStore } from 'react';

export const CLIENT_TYPES = ['home', 'industrial'];
export const ROLES        = ['enduser', 'admin'];
// The 4 shipped roles are clientType × role:
//   home-enduser · home-admin · industry-enduser · industry-admin
export const ROLE_NAMES   = ['home-enduser', 'home-admin', 'industry-enduser', 'industry-admin'];

const LS_CLIENT_TYPE = 'prvsk_client_type';
const LS_ROLE        = 'prvsk_role';

// Runtime overrides set by the (future) login hydration. Null = fall through.
let _authClientType = null;
let _authRole       = null;
// Menu permissions for the logged-in role (array of menu keys, or ['*'] = all).
// null = not yet hydrated → fail-open (show everything) until the server responds.
let _authMenus      = null;
// Menu keys the PRODUCT (box CLIENT_TYPE) leaves out, whatever the role — e.g. a
// plan with no recording. In-page buttons / settings for a feature check this
// via productHas(), not hasMenu(): a role without the Known Faces menu still
// gets the alert's "Add to Known" button on a normal home site.
let _authProductHidden = [];
// App Support page (PRVSK-109) vs the old Admin Config → Support tab. From the
// box's /ui/entitlements `app_support`: a box without it (older build, or
// APP_SUPPORT_ENABLED not in its config) keeps the old tab.
let _authAppSupport = false;
// Optional box routes this box has (/ui/entitlements box_features). The UI is
// hosted centrally and also talks to boxes on older builds, which send no list:
// anything gated on boxHas() is hidden there instead of failing.
let _authBoxFeatures = [];

// Last menus the server confirmed for this login, kept on the device. With the
// server unreachable /ui/entitlements can't answer, and fail-open would put
// every menu in the sidebar — offline, show what this account had last time.
// Cleared on login/logout (clearEntitlements) so it never outlives the session.
const LS_CACHE = 'prvsk_entitlements_cache';
try {
  const c = JSON.parse(localStorage.getItem(LS_CACHE) || 'null');
  if (c) {
    _authClientType = c.clientType || null;
    _authRole       = c.role || null;
    _authMenus      = Array.isArray(c.menus) ? c.menus : null;
    _authProductHidden = Array.isArray(c.productHidden) ? c.productHidden : [];
    _authAppSupport = !!c.appSupport;
    _authBoxFeatures = Array.isArray(c.boxFeatures) ? c.boxFeatures : [];
  }
} catch { /* corrupt / storage blocked — fall back to fail-open */ }

const _listeners = new Set();
const _emit = () => _listeners.forEach(fn => fn());

// PRVSK-65: the localStorage override is a dev/manual convenience for previewing
// a product tier before backend config catches up. If left set on a real device
// it would permanently mask the account's true role no matter how often the
// server value refreshes, so it's only honoured outside production builds.
const _DEV_OVERRIDES_ALLOWED = process.env.NODE_ENV !== 'production';

function _resolveClientType() {
  // Explicit manual/dev override wins over the server value so the console can be
  // previewed before CLIENT_TYPE is set in backend config.
  const ls = _DEV_OVERRIDES_ALLOWED && typeof localStorage !== 'undefined'
    ? (localStorage.getItem(LS_CLIENT_TYPE) || '') : '';
  if (ls) return ls;
  if (_authClientType) return _authClientType;
  if (process.env.REACT_APP_CLIENT_TYPE) return process.env.REACT_APP_CLIENT_TYPE;
  return 'home';
}

function _resolveRole() {
  const ls = _DEV_OVERRIDES_ALLOWED && typeof localStorage !== 'undefined'
    ? (localStorage.getItem(LS_ROLE) || '') : '';
  if (ls) return ls;
  if (_authRole) return _authRole;
  if (process.env.REACT_APP_ROLE) return process.env.REACT_APP_ROLE;
  return 'admin';
}

export function getClientType() { return _resolveClientType(); }
export function getRole()       { return _resolveRole(); }
export function isAdmin()       { return _resolveRole() === 'admin'; }

export function getEntitlements() {
  return { clientType: _resolveClientType(), role: _resolveRole(), menus: _authMenus,
           productHidden: _authProductHidden, appSupport: _authAppSupport,
           boxFeatures: _authBoxFeatures };
}

// True if the role may access this menu key. null menus (pre-hydration) = allow.
export function hasMenu(key) {
  if (!key) return true;
  if (_authMenus == null) return true;
  return _authMenus.includes('*') || _authMenus.includes(key);
}

// True unless this site's product leaves the feature out (see _authProductHidden).
// Role-independent — use for in-page buttons and settings of a feature.
export function productHas(key) {
  return !_authProductHidden.includes(key);
}

// True if this box has an optional route (see _authBoxFeatures).
export function boxHas(key, ent = getEntitlements()) {
  return (ent.boxFeatures || []).includes(key);
}

// Gate a nav link / route by its menu key + industrial flag.
// A link declares { menu, industrial? }. Industrial links additionally require
// the site to be industrial (clientType). No `menu` → always visible.
export function canAccessMenu(link, ent = getEntitlements()) {
  if (!link) return true;
  if (link.industrial && ent.clientType !== 'industrial') return false;
  if (link.appSupport && !ent.appSupport) return false;
  return hasMenu(link.menu);
}

// The combined role identity — one of ROLE_NAMES (e.g. "industry-admin").
export function roleName(ent = getEntitlements()) {
  const tier = ent.clientType === 'industrial' ? 'industry' : 'home';
  return `${tier}-${ent.role === 'admin' ? 'admin' : 'enduser'}`;
}

// Called from the login flow once the backend returns these fields.
export function hydrateFromAuth({ clientType, role, menus, productHidden, appSupport, boxFeatures } = {}) {
  if (clientType !== undefined) _authClientType = clientType || null;
  if (role       !== undefined) _authRole       = role || null;
  if (menus      !== undefined) _authMenus      = Array.isArray(menus) ? menus : null;
  if (productHidden !== undefined) _authProductHidden = Array.isArray(productHidden) ? productHidden : [];
  if (appSupport !== undefined) _authAppSupport = !!appSupport;
  if (boxFeatures !== undefined) _authBoxFeatures = Array.isArray(boxFeatures) ? boxFeatures : [];
  try {
    localStorage.setItem(LS_CACHE, JSON.stringify({ clientType: _authClientType, role: _authRole, menus: _authMenus,
                                                    productHidden: _authProductHidden,
                                                    appSupport: _authAppSupport,
                                                    boxFeatures: _authBoxFeatures }));
  } catch { /* storage blocked — in-memory values still apply */ }
  _emit();
}

// Session ended (logout, 401, or a new login) — drop the cached menus so the
// next account doesn't start out with this one's sidebar.
export function clearEntitlements() {
  _authClientType = null;
  _authRole       = null;
  _authMenus      = null;
  _authProductHidden = [];
  _authAppSupport = false;
  _authBoxFeatures = [];
  try { localStorage.removeItem(LS_CACHE); } catch { /* ignore */ }
  _emit();
}

// PRVSK-65: /ui/entitlements was only ever fetched once per session (on login),
// so a role edit in Users & Roles never reached an already-open session — the
// sidebar and route guards kept running off the stale menu list until logout.
// This is the single refetch path App.jsx polls/refocuses through and that
// AccessManagementPage calls right after saving a role, so every caller stays
// in sync with the same hydration logic instead of duplicating the fetch.
export function refreshEntitlements(apiFetch) {
  return apiFetch('/ui/entitlements')
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) hydrateFromAuth({ clientType: d.client_type, role: d.role, menus: d.menus,
                                          productHidden: d.product_hidden || [],
                                          // absent on older boxes → false (old Support tab)
                                          appSupport: !!d.app_support,
                                          boxFeatures: d.box_features || [] }); })
    .catch(() => {});
}

// Dev / manual override — persists so a single box can be flipped to the
// Industrial product without a backend change. Pass null to clear.
export function setClientTypeOverride(clientType) {
  if (clientType) localStorage.setItem(LS_CLIENT_TYPE, clientType);
  else            localStorage.removeItem(LS_CLIENT_TYPE);
  _emit();
}
export function setRoleOverride(role) {
  if (role) localStorage.setItem(LS_ROLE, role);
  else      localStorage.removeItem(LS_ROLE);
  _emit();
}

// ─── Gating predicate ─────────────────────────────────────────────────────────
//
// A nav link / route may carry a `requires` clause:
//   requires: { clientType: 'industrial' }        // only industrial customers
//   requires: { role: ['admin', 'operator'] }     // hide from viewers
//   requires: { clientType: 'industrial', role: ['admin'] }
// No `requires` clause → visible to everyone (unchanged current behaviour).
export function canAccess(requires, ent = getEntitlements()) {
  if (!requires) return true;
  if (requires.clientType && ent.clientType !== requires.clientType) return false;
  if (requires.role) {
    const allowed = Array.isArray(requires.role) ? requires.role : [requires.role];
    if (!allowed.includes(ent.role)) return false;
  }
  return true;
}

// ─── React binding ────────────────────────────────────────────────────────────
function _subscribe(cb) { _listeners.add(cb); return () => _listeners.delete(cb); }
function _snapshot() { return `${_resolveClientType()}|${_resolveRole()}|${_authMenus ? _authMenus.join(',') : '*'}|${_authProductHidden.join(',')}|${_authAppSupport}|${_authBoxFeatures.join(',')}`; }

// Re-renders when entitlements change (hydrate / override).
export function useEntitlements() {
  useSyncExternalStore(_subscribe, _snapshot, _snapshot);
  return getEntitlements();
}
